<template>
  <div>
    <article class="project-analysis-item__wrapper" style="position: relative">
      <AnalysisSettings
        ref="AnalysisSettings"
        :propReadOnly="propReadOnly"
        :statusDisableActionBtns="statusDisableActionBtns"
        :index="index"
        :thisAnalysisName="textAnalysisTitle"
        :isTextAnalysis="true"
        :savedNodes="textAnalysisObject.saved_node_ids"
        :contentChanged="this.contentChanged"
        :lastModified="
          formatDateTime(textAnalysisObject.last_modified_on, $store.getters.getLanguage)
        "
        @toggled="isExpanded = !isExpanded"
        @changeAnalysisTitle="renameAnalysis"
        :totalThemes="hasFetchedThemes ? sortedThemes.length : -1"
      ></AnalysisSettings>

      <!--------------------------------------------
      | CONTENT
      --------------------------------------------->
      <div v-if="isExpanded">
        <LoadingSpinner
          :isLoading="isLoadingData"
          :componentStyle="true"
          :loadingDescription="'Please wait while we fetch your questions and related data.'"
          style="position: relative; padding-left: 1rem"
          :style="{ 'min-height': minHeight }"
        />
      </div>
      <div v-if="showOldVersionWarning">
        <MessageBlock class="banner-msg" message-type="information">
          <p>
            <VisibleText
              >Warning, your dataset needs to be reconfigured for the latest version. Please
              wait.</VisibleText
            >
          </p>
          <p>
            <VisibleText>After this completes, you may need to refresh you browser.</VisibleText>
          </p>
        </MessageBlock>
      </div>
      <div v-if="!isLoadingData && !showOldVersionWarning" style="position: relative">
        <ProjectAnalysisTextTheme
          style="position: relative"
          ref="ProjectAnalysisTextThemeRef"
          v-if="sortedThemes && isExpanded && sortedResponses"
          :unsortedResponses="unsortedResponses"
          :textAnalysisObject="textAnalysisObject"
          :includeSmartCoverage="textAnalysisObject.includeSmartCoverage"
          :sortedThemes="sortedThemes"
          :themesReloading="themesReloading"
          @updateAllCharts="updateAllCharts($event)"
          :allCharts="textAnalysisObject.allCharts ? textAnalysisObject.allCharts : []"
          :propReadOnly="propReadOnly"
          :hasPreProcessedText="hasPreProcessedText"
          :themesPendingRefresh="Array.from(this.themesPendingRefresh)"
          :categoricalQuestions="allCategoryQuestions"
          @updateSelectedCustomModels="updateSelectedCustomModels($event)"
          @createSegments="createSegments($event)"
          @starSegment="onStarSegment($event)"
          @saveTextToProject="saveTextToProject"
          @updateTextObject="updateTextObject($event)"
          @updateSortOrder="updateThemeSortOrder($event)"
          @updateSortType="updateThemeSortType($event)"
          @removeTheme="removeTheme($event)"
          @refreshForInsights="refreshForInsights($event)"
          @refreshTheme="refreshTheme($event)"
          @refreshThemes="refreshThemes($event)"
          @updateTheme="updateTheme($event)"
        />
        <ProjectAnalysisTextMain
          ref="ProjectAnalysisTextMainRef"
          v-if="textAnalysisObject.uuid && sortedResponses && isExpanded"
          :textAnalysisObject="textAnalysisObject"
          :unsortedResponses="unsortedResponses"
          :includeSmartCoverage="textAnalysisObject.includeSmartCoverage"
          :datasetIDQuestions="datasetIDQuestions"
          :sortedResponses="sortedResponses"
          :propReadOnly="propReadOnly"
          :sortedThemes="sortedThemes"
          :showResponseListLoadingSpinner="showResponseListLoadingSpinner"
          :hasPreProcessedText="hasPreProcessedText"
          @filterResponsesBy="filterResponsesByThis($event)"
          :filterResponsesBy="filterResponsesBy"
          @runSortAndSelect="runSortAndSelect"
          @updateTextObject="updateTextObject($event)"
          @updateFilterParam="updateFilterParam($event)"
          @removeThisFilter="removeThisFilter($event)"
          @emitSortAndSelect="runSortAndSelect"
          @findSimilar="findSimilar"
          :searchingForWordAssociations="searchingForWordAssociations"
          @updateTheme="updateTheme($event)"
          @refreshTheme="refreshTheme($event)"
          @removeTheme="removeTheme($event)"
          @refreshAllCoverage="updateAllCoverage()"
        />
      </div>
    </article>
  </div>
</template>

<script>
// Services
import AutoService from '@/services/autoService.js';
// Components
import AnalysisSettings from '@/components/Project/ProjectAnalysis/Components/AnalysisSettings.vue';
import ProjectAnalysisTextTheme from './ProjectAnalysisTextTheme.vue';
import ProjectAnalysisTextMain from './ProjectAnalysisTextMain/ProjectAnalysisTextMain.vue';
import LoadingSpinner from '@/components/UI/Spinner.vue';
import MessageBlock from '@/components/UI/Message/MessageBlock.vue';

// Mixins
import DatasetDetailsMixin from '@/utils/mixins/datasetDetailsMixin.js';
import ProjectMixin from '@/components/Project/Mixins/projectMixin.js';
import ProjectReportMixin from '@/components/Project/Mixins/projectReportMixin.js';
import textAnalysisMixin from './Mixins/textAnalysisMixin.js';
import themeColorsMixin from './Mixins/themeColorsMixin.js';
import ThemeListMixin from '@/components/Project/ProjectAnalysis/ProjectAnalysisText/ProjectAnalysisTextMain/Mixins/themeListMixin.js';
import FiltersMixin from '@/utils/mixins/userMixin.js';
import AnalysisMixin from '@/components/Project/ProjectAnalysis/Components/analysisMixin.js';

// Helpers
import moment from 'moment';

export default {
  name: 'ProjectAnalysisTextItem',
  mixins: [
    DatasetDetailsMixin,
    ProjectMixin,
    ProjectReportMixin,
    AnalysisMixin,
    textAnalysisMixin,
    ThemeListMixin,
    themeColorsMixin,
    FiltersMixin,
  ],
  props: {
    index: {
      type: Number,
      required: true,
    },
    uuid: {
      type: String,
      required: true,
    },
    datasetIDQuestions: {
      default: () => [],
      type: Array,
    },
    propReadOnly: {
      default: () => false,
      type: Boolean,
    },
    isDatasetReadOnly: {
      default: () => false,
      type: Boolean,
    },
    expandThisAnalysis: {
      default: () => false,
      type: Boolean,
    },
    renameThisAnalysis: {
      default: () => false,
      type: Boolean,
    },
    saveThisAnalysis: {
      default: () => false,
      type: Boolean,
    },
    setToChangeSentiments: {
      default: () => false,
      type: Boolean,
    },
    setToChangeSmartCoverage: {
      default: () => false,
      type: Boolean,
    },
    checkThisAIPreprocessing: {
      default: () => false,
      type: Boolean,
    },
    slideForSaving: {
      default: () => {},
      type: Object,
    },
    slideName: {
      default: () => '',
      type: String,
    },
    setToUpdateTimeFilter: {
      default: () => false,
      type: Boolean,
    },
    selectedTimeFilter: {
      default: () => 'All data',
      type: String,
    },
    selectedTimeSeries: {
      default: () => 'Days',
      type: String,
    },
    setToUpdateTimeSeries: {
      default: () => false,
      type: Boolean,
    },
    allCategoryQuestions: {
      default: () => [],
      type: Array,
    },
  },
  components: {
    AnalysisSettings,
    MessageBlock,
    ProjectAnalysisTextTheme,
    ProjectAnalysisTextMain,
    LoadingSpinner,
  },
  data() {
    return {
      AUTO_SERVICE: new AutoService(this.$pigeonline),
      savedSlide: '',
      theseReportProjects: [], // used to find collections
      textAnalysisObject: {},
      unsortedResponses: [],
      sortedResponses: [],
      unsortedThemes: [],
      sortedThemes: [],
      themesPendingRefresh: new Set(), // Using a set to avoid duplications, consider applying this elsewhere TODO
      pollInterval: null,
      pollDelay: 200, // start fast
      hasFetchedThemes: false,
      isLoadingData: false,
      isExpanded: false,
      contentChanged: false,
      showResponseListLoadingSpinner: false,
      showOldVersionWarning: false,
      searchingForWordAssociations: '',
      themesReloading: [],
      hasPreProcessedText: false,
      filterResponsesBy: '',
    };
  },

  async created() {
    if (this.project) {
      this.isLoadingData = true;
      const refreshedTextAnalysisObject = await this.refreshTextAnalysisObject();
      if (refreshedTextAnalysisObject == true) {
        if (this.textAnalysisObject) {
          await this.checkForOldSavedNodes(); // Prior to 1.10.0, old nodes were saved directly, wasting space. Revert back to IDs
          // Successful object has a Selected Text Question
          if (this.textAnalysisObject.selectedTextQuestion) {
            //this.updateUnsortedResponses()
            let selectedTextQuestion = this.deepCloneObj(
              this.textAnalysisObject.selectedTextQuestion,
            );
            if (selectedTextQuestion._id.$oid) {
              selectedTextQuestion._id = selectedTextQuestion._id.$oid;
            }

            // This may have been updated since the last time. Such as question_text changes
            // this.refreshSelectedTextQuestion(selectedTextQuestion._id); // TODO FINISH

            // As of 1.10.0 we check the text preprocessing
            this.checkAIProcessingTextAnalysis(selectedTextQuestion._id);

            // Ensure the text question has 1.7.0 meta-data. If not, tell the user
            if (!Object.keys(selectedTextQuestion).includes('added_count')) {
              this.showOldVersionWarning = true;
              await this.regenerateTextData(selectedTextQuestion);

              if (!this.textAnalysisObject.themes || !this.textAnalysisObject.themes.length) {
                this.showOldVersionWarning = false;
              }
            }
            if (this.textAnalysisObject.themes && this.textAnalysisObject.themes.length) {
              this.showOldVersionWarning = true;
              // await this.removeOldThemes();
              this.showOldVersionWarning = false;
            }
            if (!this.textAnalysisObject.adjustedSentiments) {
              this.textAnalysisObject.adjustedSentiments = [];
            }

            // Add new fields or remove deprecated ones
            if (
              !this.textAnalysisObject.selectedThemeUUIDs ||
              !Array.isArray(this.textAnalysisObject.selectedThemeUUIDs)
            ) {
              this.textAnalysisObject.selectedThemeUUIDs = [];
            }
            if (
              this.textAnalysisObject.themesSortOrder == 'created_first' ||
              this.textAnalysisObject.themesSortOrder == 'created_last'
            ) {
              this.textAnalysisObject.themesSortOrder = 'descending';
            }
            if (!this.textAnalysisObject.themesSortType) {
              this.textAnalysisObject.themesSortType = 'coverage';
            }
            await this.runFetchTextQuestionResponses();
          }
        }
      }
      this.isLoadingData = false;
      this.checkAndApplyName();
    }
  },
  computed: {
    statusDisableActionBtns() {
      return this.isLoadingData || !this.unsortedThemes.length;
    },
    textAnalysisTitle() {
      let textTitle = '';
      if (this.textAnalysisObject) {
        if (
          this.textAnalysisObject.analysis_title &&
          this.textAnalysisObject.analysis_title.length
        ) {
          textTitle = this.textAnalysisObject.analysis_title;
        }
      }
      return textTitle;
    },
    selectedThemeUUIDs() {
      let uuid = [];
      if (this.textAnalysisObject.selectedThemeUUIDs) {
        uuid = this.textAnalysisObject.selectedThemeUUIDs;
      }
      return uuid;
    },
    childChangeInContent() {
      return this.textAnalysisObject.thisContentChanged;
    },
    minHeight() {
      if (this.isLoadingData) {
        return '5em';
      } else {
        return '0';
      }
    },
  },
  methods: {
    deepCloneObj(obj) {
      // deep clones an object using JSON stringify (data loss might occur)
      if (Array.isArray(obj)) {
        return obj.map((item) => JSON.parse(JSON.stringify(item)));
      } else if (typeof obj == 'object') {
        return JSON.parse(JSON.stringify(obj));
      }
    },
    // Connects this.textAnalysisObject to its associated project
    async refreshTextAnalysisObject() {
      if (this.project.textAnalysisObjects.length) {
        // Load text analysis from the project object
        for (let i = 0; i < this.project.textAnalysisObjects.length; i++) {
          let modifiedProject = this.deepCloneObj(this.project.textAnalysisObjects[i]);
          if (modifiedProject.uuid == this.uuid) {
            this.textAnalysisObject = this.deepCloneObj(modifiedProject);
            return true;
          }
        }
      }
      return false;
    },

    async refreshSelectedTextQuestion() {
      // TODO FINISH
    },

    async getThemes(ids = null) {
      const payload = {
        text_analysis_uuid: this.textAnalysisObject.uuid,
      };

      if (ids && ids.length) {
        payload.theme_ids = ids;
      }

      const fetchedThemes = await this.TEXT_SERVICE.fetchTextThemes(payload);

      if (fetchedThemes && fetchedThemes.length) {
        let found_colors = fetchedThemes.map((t) => t.theme_color);

        for (let theme of fetchedThemes) {
          let id = theme._id;
          if (id && id.$oid) id = id.$oid;
          theme._id = id;

          if (!theme.theme_color) {
            const new_color = await this.assignColor(found_colors);
            theme.theme_color = new_color;
            found_colors.push(new_color);
          }
        }
      }

      // Merge or replace
      if (ids && ids.length) {
        this.mergeUpdatedThemes(fetchedThemes);
      } else {
        this.unsortedThemes = fetchedThemes;
        await this.sortThemes();
        this.hasFetchedThemes = true;
      }
      if (this.$refs.ProjectAnalysisTextMainRef) {
        this.$refs.ProjectAnalysisTextMainRef.updateCounts();
      }
    },

    async mergeUpdatedThemes(updatedThemes) {
      updatedThemes.forEach((updated) => {
        const idx = this.unsortedThemes.findIndex((t) => t._id === updated._id);
        if (idx !== -1) {
          this.unsortedThemes[idx] = updated;
        } else {
          this.unsortedThemes.push(updated);
        }
      });

      await this.sortThemes();

      if (this.$refs.ProjectAnalysisTextThemeRef) {
        this.$refs.ProjectAnalysisTextThemeRef.refreshThemesFromParent();
      }
      if (this.$refs.ProjectAnalysisTextMainRef) {
        this.$refs.ProjectAnalysisTextMainRef.refreshThemesFromParent();
      }
    },

    // In legacy objects, saved_node_ids were actually full nodes, wasteful. Revert back to ids. These are just used by AnalysisSettings to recolor
    async checkForOldSavedNodes() {
      if (this.textAnalysisObject.saved_node_ids && this.textAnalysisObject.saved_node_ids.length) {
        let new_ids = [];
        for (let i = 0; i < this.textAnalysisObject.saved_node_ids.length; i++) {
          if (
            Object.keys(this.textAnalysisObject.saved_node_ids[i]).length &&
            this.textAnalysisObject.saved_node_ids[i].uuid
          ) {
            new_ids.push(this.textAnalysisObject.saved_node_ids[i].uuid);
            if (this.textAnalysisObject.saved_node_ids[i].type == 'section') {
              let content_ids = this.textAnalysisObject.saved_node_ids[i].content.map(
                (subnode) => subnode.uuid,
              );
              new_ids = new_ids.concat(content_ids);
            }
          }
        }
        if (new_ids.length) {
          this.textAnalysisObject.saved_node_ids = new_ids;
          this.saveTextToProject();
        }
      }
    },

    async checkAIProcessingTextAnalysis(id) {
      const results = await this.DATASETS_SERVICE.checkAIProcessingService({
        client_question_id: id,
      });
      if (results.status == 'complete') {
        this.hasPreProcessedText = true;
      }
    },

    async runFetchTextQuestionResponses() {
      this.isLoadingData = true;
      let selectedTextQuestion = this.deepCloneObj(this.textAnalysisObject.selectedTextQuestion);
      if (selectedTextQuestion._id.$oid) {
        selectedTextQuestion._id = selectedTextQuestion._id.$oid;
      }
      let fetchedQuestionResponses = [];
      // Responses come from another API request
      fetchedQuestionResponses = await this.fetchTextQuestionResponses(
        selectedTextQuestion,
        this.textAnalysisObject.filterParams,
      );

      if (fetchedQuestionResponses && fetchedQuestionResponses.length) {
        for (let i = 0; i < fetchedQuestionResponses.length; i++) {
          let id;
          if (fetchedQuestionResponses[i].id) {
            id = fetchedQuestionResponses[i].id;
            if (fetchedQuestionResponses[i].id.$oid) {
              id = fetchedQuestionResponses[i].id.$oid;
            }
          }
          if (fetchedQuestionResponses[i]._id) {
            id = fetchedQuestionResponses[i]._id;
            if (fetchedQuestionResponses[i]._id.$oid) {
              id = fetchedQuestionResponses[i]._id.$oid;
            }
            delete fetchedQuestionResponses[i]._id;
          }
          fetchedQuestionResponses[i].id = id;
        }
        // This is an important variable, used for all response rendering and sorting
        this.unsortedResponses = fetchedQuestionResponses;

        await this.getThemes();
        this.cleanAndSaveProject(true);
      }
      await this.runSortAndSelect();
    },
    async regenerateTextData(selectedTextQuestion) {
      await this.setActiveTab('Themes');

      await this.$services.DATASETS_SERVICE.updateClientQuestion(selectedTextQuestion._id, {
        approved_data_type: 'OPEN_ENDED',
        suggested_data_type: selectedTextQuestion.suggested_data_type,
        scale_range: null,
        unique_values: [],
      });

      let updatedClientQuestions = await this.updateClientQuestions(
        selectedTextQuestion.data_set_id,
      );
      for (let i = 0; i < updatedClientQuestions.length; i++) {
        if (updatedClientQuestions[i]._id) {
          let newID = updatedClientQuestions[i]._id;
          if (newID.$oid) {
            newID = newID.$oid;
          }
          if (newID == selectedTextQuestion._id) {
            this.textAnalysisObject.selectedTextQuestion = updatedClientQuestions[i];
            await this.saveSelectedTextQuestion({
              selectedTextQuestion: this.textAnalysisObject.selectedTextQuestion,
              forceCompute: false,
              uuid: this.textAnalysisObject.uuid,
            });
            break;
          }
        }
      }
    },
    async updateSelectedCustomModels(newModels) {
      let models = [];
      if (newModels) {
        models = newModels;
      }
      this.textAnalysisObject.customTopicModels = models;
      const indx = this.project.textAnalysisObjects.findIndex(
        (d) => d.uuid == this.textAnalysisObject.uuid,
      );
      if (indx > -1) {
        this.project.textAnalysisObjects[indx].customTopicModels =
          this.textAnalysisObject.customTopicModels;
        this.project.textAnalysisObjects[indx].last_modified_on = moment().valueOf();
        await this.cleanAndSaveProject();
      }
    },
    async createSegments(question_ids) {
      if (this.propReadOnly) return;

      // Update TAO
      this.textAnalysisObject.segment_categories = question_ids;

      const indx = this.project.textAnalysisObjects.findIndex(
        (d) => d.uuid == this.textAnalysisObject.uuid,
      );

      if (indx > -1) {
        this.project.textAnalysisObjects[indx].segment_categories = question_ids;
        this.project.textAnalysisObjects[indx].last_modified_on = moment().valueOf();

        // Save project
        await this.cleanAndSaveProject();

        // Resolve question ID
        let question_id = this.textAnalysisObject.selectedTextQuestion._id;
        if (question_id.$oid) question_id = question_id.$oid;

        // Trigger backend coverage recomputation
        await this.TEXT_SERVICE.updateThemeCoverage({
          text_analysis_uuid: this.textAnalysisObject.uuid,
        });

        // Refresh responses (for FE highlighting)
        //await this.runFetchTextQuestionResponses();

        // Refresh ALL themes
        this.refreshAllThemes();

        // Delete suggested themes as they won't have segments
        this.TEXT_SERVICE.deleteAllSuggestedThemes({
          project_id: this.project.id,
          text_analysis_uuid: this.textAnalysisObject.uuid,
        });
      }
    },

    async onStarSegment({ key, starred, theme_id }) {
      // Ensure the list exists
      if (!this.textAnalysisObject.starredSegments) {
        this.textAnalysisObject.starredSegments = [];
      }

      // If the user UN-starred the segment → remove it
      if (!starred) {
        this.textAnalysisObject.starredSegments = this.textAnalysisObject.starredSegments.filter(
          (seg) => (seg.theme_id == theme_id && seg.key !== key) || seg.theme_id !== theme_id,
        );
      } else {
        // If the user starred it → add it (only if not already present)
        const exists = this.textAnalysisObject.starredSegments.some(
          (seg) => seg.key === key && seg.theme_id === theme_id,
        );

        if (!exists) {
          this.textAnalysisObject.starredSegments.push({
            key,
            theme_id,
          });
        }
      }

      const indx = this.project.textAnalysisObjects.findIndex(
        (d) => d.uuid == this.textAnalysisObject.uuid,
      );
      if (indx > -1) {
        this.project.textAnalysisObjects[indx].starredSegments =
          this.textAnalysisObject.starredSegments;
        this.project.textAnalysisObjects[indx].last_modified_on = moment().valueOf();
        await this.cleanAndSaveProject();
      }
    },

    async updateTimeFilter(newTimeFilters) {
      let time_filter = 'All data';
      if (newTimeFilters) {
        time_filter = newTimeFilters;
      }
      this.textAnalysisObject.time_filter_name = time_filter;
      const indx = this.project.textAnalysisObjects.findIndex(
        (d) => d.uuid == this.textAnalysisObject.uuid,
      );
      if (indx > -1) {
        this.project.textAnalysisObjects[indx].time_filter_name =
          this.textAnalysisObject.time_filter_name;
        this.project.textAnalysisObjects[indx].last_modified_on = moment().valueOf();
        await this.cleanAndSaveProject();
      }
    },

    async updateThemeSortOrder(newSortOrder) {
      if (!newSortOrder) return;
      this.textAnalysisObject.themesSortOrder = newSortOrder;
      const indx = this.project.textAnalysisObjects.findIndex(
        (d) => d.uuid == this.textAnalysisObject.uuid,
      );
      if (indx > -1) {
        this.project.textAnalysisObjects[indx].themesSortOrder =
          this.textAnalysisObject.themesSortOrder;
        this.project.textAnalysisObjects[indx].last_modified_on = moment().valueOf();
        await this.cleanAndSaveProject();
      }
      await this.sortThemes();
      if (this.$refs.ProjectAnalysisTextThemeRef) {
        this.$refs.ProjectAnalysisTextThemeRef.refreshThemesFromParent();
      }
      if (this.$refs.ProjectAnalysisTextMainRef) {
        this.$refs.ProjectAnalysisTextMainRef.refreshThemesFromParent();
      }
    },
    async updateThemeSortType(newSortType) {
      if (!newSortType) return;
      this.textAnalysisObject.themesSortType = newSortType;
      const indx = this.project.textAnalysisObjects.findIndex(
        (d) => d.uuid == this.textAnalysisObject.uuid,
      );
      if (indx > -1) {
        this.project.textAnalysisObjects[indx].themesSortType =
          this.textAnalysisObject.themesSortType;
        this.project.textAnalysisObjects[indx].last_modified_on = moment().valueOf();
        await this.cleanAndSaveProject();
      }
      await this.sortThemes();
      if (this.$refs.ProjectAnalysisTextThemeRef) {
        this.$refs.ProjectAnalysisTextThemeRef.refreshThemesFromParent();
      }
      if (this.$refs.ProjectAnalysisTextMainRef) {
        this.$refs.ProjectAnalysisTextMainRef.refreshThemesFromParent();
      }
    },
    async findSimilar() {
      if (
        this.textAnalysisObject.search.searchString &&
        this.textAnalysisObject.datasetId &&
        this.selectedTextQuestionId
      ) {
        this.searchingForWordAssociations =
          'Searching for words similar to: ' + this.textAnalysisObject.search.searchString;
        const alsoFound = await this.fetchWordAssociations(
          this.textAnalysisObject.search.searchString,
          this.textAnalysisObject.datasetId,
          this.selectedTextQuestionId,
        );
        if (alsoFound) {
          this.textAnalysisObject.search.alsoFound = alsoFound;
        }
        this.searchingForWordAssociations = '';
      }
    },

    async changeTimeFilter(timeFilter) {
      if (!timeFilter) return;

      if (this.dataset.created_on_column_id) {
        let question_id = this.dataset.created_on_column_id;
        let filter_params = {};
        if (
          this.textAnalysisObject.filter_params &&
          Object.keys(this.textAnalysisObject.filter_params).length &&
          this.textAnalysisObject.filter_params['operands']
        ) {
          filter_params = this.deepCloneObj(this.textAnalysisObject.filter_params);
        } else {
          filter_params = {
            client_question_id: question_id,
            operands: [],
            global_combine_action: 'and',
          };
        }
        const new_filter_params = await this.$_analysisMixin_calculateNewTimeFilters(
          filter_params,
          timeFilter,
        );
        await this.updateTimeFilter(timeFilter);
        this.updateFilterParam(new_filter_params);
      }
    },

    // Some objects depend on textAnalysisObject as a prop, so we update it here when child components update their own version of it
    // This will also scroll towards the middle of the screen
    // TODO better to send textAnalysisObject instead of full project?
    async updateTextObject(object) {
      let project = object.project;
      let scroll = object.scroll;
      if (project) {
        if (project.textAnalysisObjects.length) {
          for (let i = 0; i < project.textAnalysisObjects.length; i++) {
            if (project.textAnalysisObjects[i].uuid == this.textAnalysisObject.uuid) {
              this.textAnalysisObject.selectedThemeUUIDs =
                project.textAnalysisObjects[i].selectedThemeUUIDs;
              this.textAnalysisObject.selectedResponseIds =
                project.textAnalysisObjects[i].selectedResponseIds;
              this.textAnalysisObject.search = project.textAnalysisObjects[i].search;
              if (project.textAnalysisObjects[i].expandedResponses) {
                this.textAnalysisObject.expandedResponses =
                  project.textAnalysisObjects[i].expandedResponses;
              }
              if (project.textAnalysisObjects[i].hiddenComments) {
                this.textAnalysisObject.hiddenComments =
                  project.textAnalysisObjects[i].hiddenComments;
              }
              if (project.textAnalysisObjects[i].bannedComments) {
                this.textAnalysisObject.bannedComments =
                  project.textAnalysisObjects[i].bannedComments;
                await this.runFetchTextQuestionResponses();
                await this.$ref;
              }
              if (project.textAnalysisObjects[i].bannedKeywords) {
                this.textAnalysisObject.bannedKeywords =
                  project.textAnalysisObjects[i].bannedKeywords;
              }
              if (project.textAnalysisObjects[i].includeSentiments) {
                this.textAnalysisObject.includeSentiments =
                  project.textAnalysisObjects[i].includeSentiments;
              }
              if (project.textAnalysisObjects[i].includeSmartCoverage) {
                this.textAnalysisObject.includeSmartCoverage =
                  project.textAnalysisObjects[i].includeSmartCoverage;
              }
              if (project.textAnalysisObjects[i].adjustedSentiments) {
                this.textAnalysisObject.adjustedSentiments =
                  project.textAnalysisObjects[i].adjustedSentiments;
              }
              if (project.textAnalysisObjects[i].activeTab) {
                this.textAnalysisObject.activeTab = project.textAnalysisObjects[i].activeTab;
              }
              if (project.textAnalysisObjects[i].themeSortOrder) {
                this.textAnalysisObject.themeSortOrder =
                  project.textAnalysisObjects[i].themeSortOrder;
              }
              if (project.textAnalysisObjects[i].themeSortType) {
                this.textAnalysisObject.themeSortType =
                  project.textAnalysisObjects[i].themeSortType;
              }
              if (project.textAnalysisObjects[i].customTopicModels) {
                this.textAnalysisObject.customTopicModels =
                  project.textAnalysisObjects[i].customTopicModels;
              }
              if (project.textAnalysisObjects[i].time_filter_name) {
                this.textAnalysisObject.time_filter_name =
                  project.textAnalysisObjects[i].time_filter_name;
              }
              if (project.textAnalysisObjects[i].filterParams) {
                this.textAnalysisObject.filterParams = project.textAnalysisObjects[i].filterParams;
              }
              if (project.textAnalysisObjects[i].time_series) {
                this.textAnalysisObject.time_series = project.textAnalysisObjects[i].time_series;
              }
              if (project.textAnalysisObjects[i].allCharts) {
                this.textAnalysisObject.allCharts = project.textAnalysisObjects[i].allCharts;
              }
              if (
                project.textAnalysisObjects[i].sentimentSortOrder <= 0 ||
                project.textAnalysisObjects[i].sentimentSortOrder > 0
              ) {
                this.textAnalysisObject.sentimentSortOrder =
                  project.textAnalysisObjects[i].sentimentSortOrder;
              }

              if (scroll == 'main' && document.getElementById('project-analysis-text-main')) {
                document.getElementById('project-analysis-text-main').scrollIntoView();
              }
              if (scroll == 'themes' && document.getElementById('project-analysis-text-theme')) {
                document.getElementById('project-analysis-text-theme').scrollIntoView();
              }
              break;
            }
          }
        }
      }
    },

    /**
     * Applies a patch-style update to a single theme.
     *
     * This method receives a "patch" object emitted from child components.
     * A patch describes only what changed, rather than sending the full theme.
     *
     * Supported patch fields:
     *   - _id: string (required)
     *   - keywords_to_add: [string]       → add canonical keywords (triggers async coverage)
     *   - keywords_to_remove: [string]    → remove canonical keywords (triggers async coverage)
     *   - fields_to_update: { key: value } → update non-keyword fields (no async coverage)
     *
     * Behavior:
     *   1. Sends the patch to saveThemes() as a minimal update payload.
     *   2. If keywords were added or removed:
     *        - Marks the theme as pending refresh
     *        - Starts async polling until coverage is recomputed
     *   3. If only non-keyword fields changed:
     *        - Fetches the updated theme immediately (no async coverage)
     *
     * This keeps theme updates atomic, race-condition-proof, and consistent
     * with the backend's patch-style update model.
     */
    async updateTheme(patch) {
      const payload = {
        themes: [
          {
            _id: patch._id,
            text_analysis_uuid: this.textAnalysisObject.uuid,
            ...patch,
          },
        ],
      };
      await this.TEXT_SERVICE.saveThemes(payload);

      // Keyword changes → async coverage → polling
      if (
        patch.keywords_to_add ||
        patch.keywords_to_remove ||
        patch.notes_to_add ||
        patch.notes_to_remove ||
        patch.dropped_to_add ||
        patch.dropped_to_remove
      ) {
        this.refreshTheme(patch._id);
        return;
      }

      // Non-keyword changes → no async coverage → fetch only this theme
      if (patch.fields_to_update) {
        await this.getThemes([patch._id]);
      }
    },

    refreshTheme(theme_id) {
      if (!theme_id) return;
      this.themesPendingRefresh.add(theme_id);
      this.startPolling();
    },

    refreshThemes(theme_ids) {
      theme_ids.forEach((id) => this.themesPendingRefresh.add(id));
      this.startPolling();
    },

    startPolling() {
      if (this.pollInterval) return; // already polling

      const poll = async () => {
        // If nothing pending, stop polling
        if (this.themesPendingRefresh.size === 0) {
          this.pollInterval = null;
          this.pollDelay = 200;
          return;
        }

        const payload = {
          theme_ids: Array.from(this.themesPendingRefresh),
        };

        let statuses;
        try {
          statuses = await this.TEXT_SERVICE.pollThemesForUpdate(payload);
        } catch (e) {
          this.pollInterval = null;
          return;
        }

        const readyIds = [];

        for (const [id, status] of Object.entries(statuses)) {
          if (status === 'ready') {
            readyIds.push(id);
            this.themesPendingRefresh.delete(id);
          }
        }

        // If ANY themes became ready, fetch them
        if (readyIds.length > 0) {
          // If many still pending → fetch all once
          if (this.themesPendingRefresh.size > 10) {
            await this.getThemes(); // fetch all
          } else {
            await this.getThemes(readyIds); // fetch only updated
          }
        }

        // If all themes are now ready → final full refresh
        if (this.themesPendingRefresh.size === 0) {
          await this.getThemes(); // ensure full consistency
          this.pollInterval = null;
          this.pollDelay = 200;
          return;
        }

        // Otherwise continue polling
        this.pollDelay = Math.min(this.pollDelay + 200, 1000);
        this.pollInterval = setTimeout(poll, this.pollDelay);
      };

      poll(); // immediate first poll
    },

    async replacingUnsortedThemes(themes) {
      let clonedThemes = this.deepCloneObj(this.unsortedThemes);
      for (let i = 0; i < themes.length; i++) {
        if (themes[i]._id.$oid) {
          themes[i]._id = themes[i]._id.$oid;
        }
        if (themes[i].id) {
        }
        let found_match = false;
        for (let x = 0; x < clonedThemes.length; x++) {
          if (clonedThemes[x]._id.$oid) {
            clonedThemes[x]._id = clonedThemes[x]._id.$oid;
          }
          if (clonedThemes[x]._id == themes[i]._id) {
            clonedThemes[x] = themes[i];
            found_match = true;
          }
        }
        if (found_match == false || !clonedThemes.length) {
          clonedThemes.unshift(themes[i]);
        }
      }
      this.unsortedThemes = clonedThemes;
      await this.sortThemes();
      if (this.$refs.ProjectAnalysisTextThemeRef) {
        this.$refs.ProjectAnalysisTextThemeRef.refreshThemesFromParent();
      }
      if (this.$refs.ProjectAnalysisTextMainRef) {
        this.$refs.ProjectAnalysisTextMainRef.refreshThemesFromParent();
        this.$refs.ProjectAnalysisTextMainRef.updateCounts();
      }
    },

    async updateAllCoverage() {
      // Trigger backend coverage recomputation
      await this.TEXT_SERVICE.updateThemeCoverage({
        text_analysis_uuid: this.textAnalysisObject.uuid,
      });

      // Refresh ALL themes
      this.refreshAllThemes();
    },

    async updateFilterParam(filter_param) {
      if (this.propReadOnly) return;

      this.textAnalysisObject.filterParams = filter_param;

      const indx = this.project.textAnalysisObjects.findIndex(
        (d) => d.uuid == this.textAnalysisObject.uuid,
      );

      if (indx > -1) {
        this.project.textAnalysisObjects[indx].filterParams = filter_param;
        this.project.textAnalysisObjects[indx].last_modified_on = moment().valueOf();

        // Save project
        await this.cleanAndSaveProject();

        // Trigger backend coverage recomputation
        await this.TEXT_SERVICE.updateThemeCoverage({
          text_analysis_uuid: this.textAnalysisObject.uuid,
        });

        // Refresh responses (for FE highlighting)
        await this.runFetchTextQuestionResponses();

        // Refresh ALL themes
        this.refreshAllThemes();

        // Also clear suggested_themes as they may not match anymore
        this.TEXT_SERVICE.deleteAllSuggestedThemes({
          project_id: this.project.id,
          text_analysis_uuid: this.textAnalysisObject.uuid,
        });
      }
    },

    async removeThisFilter(filter_param) {
      if (this.propReadOnly) return;

      // 1. Clone and modify filterParams
      let newFilterParams = this.deepCloneObj(this.textAnalysisObject.filterParams);

      if (newFilterParams.operands.length === 1) {
        newFilterParams = {};
      } else {
        newFilterParams.operands.splice(filter_param.filter_index, 1);
      }

      // 2. Update local TA object
      this.textAnalysisObject.filterParams = newFilterParams;

      // 3. Update project TA object
      const indx = this.project.textAnalysisObjects.findIndex(
        (d) => d.uuid == this.textAnalysisObject.uuid,
      );

      if (indx > -1) {
        this.project.textAnalysisObjects[indx].filterParams = newFilterParams;
        this.project.textAnalysisObjects[indx].last_modified_on = moment().valueOf();

        // 4. Save project
        if (!this.propReadOnly) {
          await this.cleanAndSaveProject();
        }

        // 5. Determine correct question_id
        let question_id = this.textAnalysisObject.selectedTextQuestion._id;
        if (question_id && question_id.$oid) {
          question_id = question_id.$oid;
        }

        // 6. Trigger backend coverage recomputation
        await this.TEXT_SERVICE.updateThemeCoverage({
          text_analysis_uuid: this.textAnalysisObject.uuid,
        });

        // 7. Refresh responses (for FE highlighting)
        await this.runFetchTextQuestionResponses();

        // 8. Refresh ALL themes
        this.refreshAllThemes();

        // Also clear suggested_themes as they may not match anymore
        this.TEXT_SERVICE.deleteAllSuggestedThemes({
          project_id: this.project.id,
          text_analysis_uuid: this.textAnalysisObject.uuid,
        });
      }
    },

    refreshAllThemes() {
      const ids = this.unsortedThemes.map((t) => t._id);
      for (const id of ids) {
        this.themesPendingRefresh.add(id);
      }
      this.startPolling();
    },

    async removeTheme(theme_id) {
      if (theme_id) {
        await this.themeRemoval(theme_id);
        if (this.$refs.ProjectAnalysisTextThemeRef) {
          this.$refs.ProjectAnalysisTextThemeRef.refreshThemesFromParent();
        }
        if (this.$refs.ProjectAnalysisTextMainRef) {
          this.$refs.ProjectAnalysisTextMainRef.refreshThemesFromParent();
        }
      }
    },
    async themeRemoval(theme_id) {
      if (theme_id) {
        let updatedThemes = [];
        let clonedThemes = this.deepCloneObj(this.unsortedThemes);
        for (let x = 0; x < clonedThemes.length; x++) {
          if (theme_id.$oid) {
            theme_id = theme_id.$oid;
          }
          if (clonedThemes[x]._id.$oid) {
            clonedThemes[x]._id = clonedThemes[x]._id.$oid;
          }
          if (clonedThemes[x]._id !== theme_id) {
            updatedThemes.push(clonedThemes[x]);
          }
        }
        this.unsortedThemes = updatedThemes;
        await this.removeChartInsights();
        this.sortThemes();
      }
    },
    async removeChartInsights() {
      if (!this.propReadOnly) {
        this.textAnalysisObject.chart_insights = []; // Assume a change, force user to regenerate insights
        const indx = this.project.textAnalysisObjects.findIndex(
          (d) => d.uuid == this.textAnalysisObject.uuid,
        );
        if (indx > -1) {
          this.project.textAnalysisObjects[indx].chart_insights =
            this.textAnalysisObject.chart_insights;
          this.project.textAnalysisObjects[indx].last_modified_on = moment().valueOf();
          await this.cleanAndSaveProject();
        }
      }
    },

    check_response_for_selected_themes(response, themes) {
      let foundMatches = 0;
      const responseId = response.id;

      for (let i = 0; i < themes.length; i++) {
        const theme = themes[i];

        // 1. Choose the correct coverage block
        const cov =
          this.textAnalysisObject.filterParams &&
          Object.keys(this.textAnalysisObject.filterParams).length
            ? theme.filtered_coverage
            : theme.coverage;

        let matched = false;

        // 2. Authoritative match
        if (cov.matching_responses && cov.matching_responses.includes(responseId)) {
          matched = true;
        }

        // 3. Authoritative dropped (include dropped in the full list)
        const isDropped = Array.isArray(theme.dropped) && theme.dropped.includes(responseId);
        const isMLDropped =
          Array.isArray(theme.ml_dropped) && theme.ml_dropped.includes(responseId);
        if (isDropped || isMLDropped) {
          matched = true;
        }

        // 4. Notes always count (override dropped)
        if (Array.isArray(theme.notes) && theme.notes.some((n) => n.responseId === responseId)) {
          matched = true;
        }

        // 5. Keyword fallback
        const keywordMatches = cov.matching_keywords || [];
        if (keywordMatches.some((x) => response.response.toLowerCase().includes(x))) {
          matched = true;
        }

        if (matched) {
          foundMatches++;
        }
      }

      return foundMatches;
    },

    async runSortAndSelect() {
      // This filters raw responses by:
      // - Is there a selection of responseIds? (e.g., from the chart you can select a range of ids)
      // - Is there a theme selected? (pressing 'filter' on a theme)
      // - Is there a search
      // Sends for final sorting
      // Note, dropped are not filtered out, because the UI needs to see them
      this.isLoadingData = true;

      let responsesToUse = this.deepCloneObj(this.unsortedResponses);
      // Text responses have been fetched

      if (responsesToUse.length) {
        let useTheseResponses = [];

        // if a response has been selected (such as from the charts)

        if (this.textAnalysisObject.selectedResponseIds.length) {
          useTheseResponses = responsesToUse.filter((response) =>
            this.textAnalysisObject.selectedResponseIds.some((id) => response.id == id),
          );
          // no selectedResponseIds (no chart selection)
        } else {
          // theme(s) have been selected
          if (this.selectedThemeUUIDs && this.selectedThemeUUIDs.length) {
            const filteredThemes = this.sortedThemes.filter((theme) =>
              this.selectedThemeUUIDs.includes(theme._id),
            );
            useTheseResponses = responsesToUse.filter((response) =>
              this.check_response_for_selected_themes(response, filteredThemes),
            );
          } else {
            useTheseResponses = responsesToUse;
          }
        }

        if (
          useTheseResponses.length &&
          this.textAnalysisObject.search &&
          this.textAnalysisObject.search.searchString.length
        ) {
          // Normal search is treated as a keyword match, this will then look for exact matches
          if (
            this.textAnalysisObject.search.searchMode &&
            this.textAnalysisObject.search.searchMode == 'exact matches' &&
            this.textAnalysisObject.search.searchResults
          ) {
            useTheseResponses = useTheseResponses.filter((response) =>
              this.textAnalysisObject.search.searchResults.some((match) =>
                response.response.toLowerCase().includes(match),
              ),
            );
            await this.sortTextResponses(useTheseResponses);
          }
          // Advanced search is similar but uses findSimilarSentences
          if (
            this.textAnalysisObject.search.searchMode &&
            this.textAnalysisObject.search.searchMode == 'similar sentences' &&
            this.textAnalysisObject.search.similarSentences
          ) {
            useTheseResponses = useTheseResponses.filter((response) =>
              this.textAnalysisObject.search.similarSentences.some((match) =>
                response.response.toLowerCase().includes(match),
              ),
            );
            await this.sortTextResponses(useTheseResponses);
          }
        } else {
          await this.sortTextResponses(useTheseResponses);
        }
      }
      this.isLoadingData = false;
    },
    filterResponsesByThis(sortBy) {
      // Toggle: if already selected, clear it
      this.filterResponsesBy = this.filterResponsesBy === sortBy ? '' : sortBy;

      //this.runSortAndSelect();
    },

    // Used by report to generate sentiments
    findSentiments(theme) {
      if (!theme) return [];

      // 1. Determine which list to use (filtered vs unfiltered)
      const hasFilter =
        this.textAnalysisObject.filterParams &&
        Object.keys(this.textAnalysisObject.filterParams).length > 0;

      const matchingIds = hasFilter
        ? theme.filtered_coverage?.matching_responses || []
        : theme.coverage?.matching_responses || [];

      // 2. Map IDs → actual response objects
      const matchedResponses = this.unsortedResponses.filter(
        (response) =>
          matchingIds.includes(response.id) ||
          (response.old_id && matchingIds.includes(response.old_id)),
      );

      // 3. Extract sentiment scores
      return matchedResponses
        .map((response) => response.response_sentiment_bert?.compound_score)
        .filter((v) => v !== undefined && v !== null);
    },
    async fetchWordAssociations(searchString, datasetId, questionId) {
      if (!searchString) return [];
      try {
        const response = await this.TEXT_SERVICE.wordAssociations({
          user_string: searchString,
          data_set_id: datasetId,
          client_question_id: questionId,
        });
        return response;
      } catch (e) {
        throw new Error('AlsoFound.vue:fetchWordAssociations ' + e.message);
      }
    },
    async save(selectedSlide, newSlideName) {
      if (this.propReadOnly) return;
      const uuid = await this.saveTextToReport(selectedSlide, newSlideName);
      if (!this.project.reportSaves) {
        this.project.reportSaves = 1;
      } else {
        this.project.reportSaves = this.project.reportSaves + 1;
      }
      await this.saveTextToProject(uuid);
      this.$emit('refresh');
    },
    async saveTextToReport(selectedSlide, newSlideName) {
      try {
        this.isLoadingData = true;
        let createdReportID = await this.createReportIfEmpty(selectedSlide, newSlideName); // the above function will do nothing under conditions of emptiness
        if (selectedSlide && selectedSlide.id) {
          createdReportID = selectedSlide.id;
        }
        let textTableData = {
          columnNames: ['Themes', 'Keywords', 'Coverage (%)', 'Coverage (/)'],
          rowNames: [],
          data: [],
        };

        //
        if (this.textAnalysisObject.includeSentiments == 'include' && this.hasPreProcessedText) {
          textTableData.columnNames.push('Sentiment');
        }
        let thisTitle = '';
        if (this.textAnalysisObject.selectedTextQuestion.question_title) {
          thisTitle = this.textAnalysisObject.selectedTextQuestion.question_title;
          if (this.textAnalysisObject.selectedTextQuestion.question_text) {
            thisTitle =
              thisTitle + ' - ' + this.textAnalysisObject.selectedTextQuestion.question_text;
          }
        } else {
          if (this.textAnalysisObject.selectedTextQuestion.question_text) {
            thisTitle = this.textAnalysisObject.selectedTextQuestion.question_text;
          }
        }
        let hasSentiments = false;
        let rowNames = [];
        for (let i = 0; i < this.sortedThemes.length; i++) {
          rowNames.push(this.sortedThemes[i].name);

          const kos = this.sortedThemes[i].keyword_objects || [];
          const keywords = kos.map((ko) => ko.keyword).join(', ');

          let coverage =
            this.parseCoveragePercentage(
              this.sortedThemes[i],
              this.unsortedResponses.length - this.textAnalysisObject.bannedComments.length,
            ) + '%';
          let coverageFractions = {};
          if (
            this.sortedThemes[i].filtered_coverage &&
            Object.keys(this.sortedThemes[i].filtered_coverage).length
          ) {
            coverageFractions =
              this.sortedThemes[i].filtered_coverage.total + '/' + this.unsortedResponses.length;
          } else {
            coverageFractions =
              this.sortedThemes[i].coverage.total + '/' + this.unsortedResponses.length;
          }
          let sentiment = {
            respondent_sentiment: {},
          };
          let thisRow = [];
          thisRow.push(keywords);
          thisRow.push(coverage);
          thisRow.push(coverageFractions);
          if (this.textAnalysisObject.includeSentiments == 'include' && this.hasPreProcessedText) {
            sentiment.respondent_sentiment = this.findSentiments(this.sortedThemes[i]);
            thisRow.push(sentiment);
            hasSentiments = true;
          }

          textTableData.data.push(thisRow);
        }
        textTableData.rowNames = rowNames;
        let textChartData = [];
        if (this.textAnalysisObject.allCharts && this.textAnalysisObject.allCharts.length) {
          textChartData = this.textAnalysisObject.allCharts;
        }
        const returnObject = await this.updateReportWithText(
          this.textAnalysisObject.uuid,
          createdReportID,
          textTableData,
          thisTitle,
          textChartData,
          hasSentiments,
        );
        this.isLoadingData = false;
        this.$emit('updateSlides');
        this.onSuccessfulSave();
        let node_ids = returnObject.newNodes.map((node) => node.uuid);
        this.textAnalysisObject.saved_node_ids = node_ids;
        return returnObject.uuid;
      } catch (e) {
        throw new Error('ProjectAnalysisText:saveTextToReport ');
      }
    },
    async saveTextToProject() {
      if (this.propReadOnly) return;
      const indx = this.project.textAnalysisObjects.findIndex(
        (d) => d.uuid == this.textAnalysisObject.uuid,
      );
      if (indx > -1) {
        this.project.textAnalysisObjects[indx].saved_node_ids =
          this.textAnalysisObject.saved_node_ids;
        this.project.textAnalysisObjects[indx].last_modified_on = moment().valueOf();
        await this.cleanAndSaveProject();
      }
    },

    checkAndApplyName() {
      if (
        !this.textAnalysisObject.analysis_title ||
        !this.textAnalysisObject.analysis_title.length
      ) {
        let textTitle = '';
        if (this.textAnalysisObject) {
          if (this.textAnalysisObject.selectedTextQuestion) {
            if (this.textAnalysisObject.selectedTextQuestion.question_title.length) {
              textTitle = this.textAnalysisObject.selectedTextQuestion.question_title;
            }
            if (
              this.textAnalysisObject.selectedTextQuestion.question_title.length &&
              this.textAnalysisObject.selectedTextQuestion.question_text.length
            ) {
              textTitle += ' - ';
            }
            if (this.textAnalysisObject.selectedTextQuestion.question_text.length) {
              textTitle += this.textAnalysisObject.selectedTextQuestion.question_text;
            }
          }
        }
        if (textTitle.length) {
          this.renameAnalysis(textTitle);
        }
      }
    },
    async renameAnalysis(title) {
      if (this.propReadOnly) return;
      const indx = this.project.textAnalysisObjects.findIndex(
        (d) => d.uuid == this.textAnalysisObject.uuid,
      );
      if (indx > -1) {
        this.project.textAnalysisObjects[indx].analysis_title = title;
        this.project.textAnalysisObjects[indx].last_modified_on = moment().valueOf();
        await this.cleanAndSaveProject();
      }
    },
    async expand() {
      this.isExpanded = !this.isExpanded;
    },
    /************
     *
     * Confirmation modals - Not unified in use!
     *
     ***********/
    onSuccessfulSave() {
      this.setConfirmText({
        btn: 'okay',
        title: 'Text analysis saved',
        message: 'Text analysis successfully saved to report.',
      });
      this.setConfirmType('success');
      this.setConfirmSourceComponent('text');
      this.setConfirmIsVisible(true);
    },
    formatDateTime: (value, language) => {
      let currentLang = '';
      if (language) {
        currentLang = language;
      }
      if (value) {
        if (currentLang === 'fr') {
          return moment(value).locale('fr').format('MMMM D, YYYY hh:mm A').toString();
        } else {
          return moment(value).format('MMMM D, YYYY hh:mm A').toString();
        }
      }
    },

    async refreshForInsights() {
      if (this.project.textAnalysisObjects && this.project.textAnalysisObjects.length) {
        this.textAnalysisObject = _.cloneDeep(this.project.textAnalysisObjects[this.index]);
      }
    },

    async updateSentimentSetting(include) {
      if (this.propReadOnly) return;

      // Update TAO
      this.textAnalysisObject.includeSentiments = include ? 'include' : 'exclude';

      const indx = this.project.textAnalysisObjects.findIndex(
        (d) => d.uuid == this.textAnalysisObject.uuid,
      );

      if (indx > -1) {
        this.project.textAnalysisObjects[indx].includeSentiments = include ? 'include' : 'exclude';
        this.project.textAnalysisObjects[indx].last_modified_on = moment().valueOf();

        // Save project
        await this.cleanAndSaveProject();

        // Resolve question ID
        let question_id = this.textAnalysisObject.selectedTextQuestion._id;
        if (question_id.$oid) question_id = question_id.$oid;

        // Trigger backend coverage recomputation
        await this.TEXT_SERVICE.updateThemeCoverage({
          text_analysis_uuid: this.textAnalysisObject.uuid,
        });

        // Refresh responses (for FE highlighting)
        //await this.runFetchTextQuestionResponses();

        // Refresh ALL themes
        this.refreshAllThemes();

        // Clear suggested themes
        this.TEXT_SERVICE.deleteAllSuggestedThemes({
          project_id: this.project.id,
          text_analysis_uuid: this.textAnalysisObject.uuid,
        });
      }
    },

    async updateTimeSeriesSetting(timeSeriesMode) {
      if (this.propReadOnly) return;

      // Update TAO
      this.textAnalysisObject.time_series = timeSeriesMode;

      const indx = this.project.textAnalysisObjects.findIndex(
        (d) => d.uuid == this.textAnalysisObject.uuid,
      );

      if (indx > -1) {
        this.project.textAnalysisObjects[indx].time_series = timeSeriesMode;
        this.project.textAnalysisObjects[indx].last_modified_on = moment().valueOf();

        // Save project
        await this.cleanAndSaveProject();

        // Resolve question ID
        let question_id = this.textAnalysisObject.selectedTextQuestion._id;
        if (question_id.$oid) question_id = question_id.$oid;

        // Trigger backend coverage recomputation
        await this.TEXT_SERVICE.updateThemeCoverage({
          text_analysis_uuid: this.textAnalysisObject.uuid,
        });

        // Refresh responses (for FE highlighting)
        //await this.runFetchTextQuestionResponses();

        // Refresh ALL themes
        this.refreshAllThemes();

        // Do NOT delete suggested themes
        // Time-series does not affect suggested themes
      }
    },
  },
  watch: {
    childChangeInContent: async function (val) {
      if (!val) return;
      this.contentChanged = true;
    },
    sortMode: async function (newVal, oldVal) {
      // don't sort the first time
      if (oldVal >= -1 && oldVal <= 1 && newVal >= -1 && newVal <= 1) {
        this.isLoadingData = true;
        if (this.unsortedResponses.length) {
          this.runSortAndSelect();
        }
        this.isLoadingData = false;
      }
    },
    currentSelectedResponseIds: async function (newVal, oldVal) {
      if (oldVal && newVal) {
        this.runSortAndSelect();
      }
    },
    hasSearchResults: async function (val) {
      if (val) {
        if (this.textAnalysisObject.search.searchResultsIds.length) {
          this.runSortAndSelect();
        }
      }
    },
    selectedThemeUUIDs: async function () {
      this.runSortAndSelect();
    },
    expandThisAnalysis: async function (val) {
      if (val) {
        this.isExpanded = true;
      } else {
        this.isExpanded = false;
      }
    },

    renameThisAnalysis: async function (val) {
      if (val) {
        if (this.$refs.AnalysisSettings) {
          this.$refs.AnalysisSettings.toggleRename();
        }
      }
    },
    saveThisAnalysis: async function (val) {
      if (val) {
        this.save(this.slideForSaving, this.slideName);
      }
    },
    setToChangeSentiments: async function (val) {
      if (val) {
        if (this.textAnalysisObject.includeSentiments == 'include') {
          await this.updateSentimentSetting(false);
          this.$emit('updateIncludeSentiment', false);
        } else {
          await this.updateSentimentSetting(true);
          this.$emit('updateIncludeSentiment', true);
        }
      }
    },
    setToChangeSmartCoverage: async function (val) {
      if (val) {
        if (this.textAnalysisObject.includeSmartCoverage == 'include') {
          await this.setIncludeSmartCoverage('exclude');
          this.$emit('updateIncludeSmartCoverage', false);
        } else {
          await this.setIncludeSmartCoverage('include');
          this.$emit('updateIncludeSmartCoverage', true);
        }

        this.updateAllCoverage();
      }
    },
    checkThisAIPreprocessing: async function (val) {
      if (val) {
        if (
          this.textAnalysisObject.selectedTextQuestion &&
          this.textAnalysisObject.selectedTextQuestion._id
        ) {
          let question_id = this.textAnalysisObject.selectedTextQuestion._id;
          if (this.textAnalysisObject.selectedTextQuestion._id.$oid) {
            question_id = this.textAnalysisObject.selectedTextQuestion._id.$oid;
          }
          this.checkAIProcessingTextAnalysis(question_id);
        }
      }
    },
    setToUpdateTimeFilter: async function (val) {
      if (val) {
        await this.changeTimeFilter(this.selectedTimeFilter);
        this.$emit('updateTimeFilter', this.selectedTimeFilter);
      }
    },
    setToUpdateTimeSeries: async function (val) {
      if (val) {
        await this.updateTimeSeriesSetting(this.selectedTimeSeries);
        this.$emit('updateTimeSeries', this.selectedTimeSeries);
      }
    },
  },
};
</script>
